import { apiRequest } from "@/api/ApiRequest";
import type {
  CmmnCodeOptionType,
  CmmnCodeType,
  DeptType,
  StaffType,
  StudentType,
  CudResultType,
} from "@/types/cmmn/CmmnTypes";
import { ApiMethod } from "@/types/Enums";
import type { ExcelHeader } from "@/types/ExcelTypes";
import type { AbilityTypes } from "@/types/system/AbilityTypes";

const _url = "/api/v1/cmmn";

/**
 * summary: Get Code List
 * #GET./api/v1/cmmn/code
 */
export const getCmmnList = async (
  params: ParamsType,
): Promise<CmmnCodeType[]> => {
  return await apiRequest<CmmnCodeType[]>({
    url: _url + "/code",
    params: params,
  });
};

/**
 * summary: Get Cmmn Code Option
 * #GET./api/v1/cmmn/code-option
 */
export const getCmmnCdOption = async (
  params: ParamsType,
): Promise<Record<string, CmmnCodeOptionType[]>> => {
  if (!params.upCdArr) {
    console.warn("upCdArr is null");
    throw new Error("upCdArr is required");
  }

  const upCds = params.upCdArr as string[];
  if (upCds.length === 0) {
    console.warn("upCdArr length is 0");
    throw new Error("upCdArr length is 0");
  }
  return await apiRequest<Record<string, CmmnCodeOptionType[]>>({
    url: _url + "/code-option",
    params: params,
  });
};

/**
 * summary: Check Duplicate Value
 * #GET./api/v1/cmmn/dup-value
 */
export const chkDupValue = async (params: ParamsType): Promise<string[]> => {
  return await apiRequest<string[]>({
    method: ApiMethod.GET,
    url: _url + "/dup-value",
    params: params,
  });
};

export const uploadExcel = async (
  tableName: string,
  data: { headers: ExcelHeader[]; data: ParamsType[] },
): Promise<CudResultType> => {
  if (!tableName) {
    console.warn("tableName is null");
    throw new Error("tableName is required");
  }
  if (data.data.length === 0) {
    console.warn("data is empty");
    throw new Error("data is required");
  }
  if (data.headers.length === 0) {
    console.warn("headers is empty");
    throw new Error("headers is required");
  }
  return await apiRequest<CudResultType>({
    method: ApiMethod.POST,
    url: "/api/v1/cmmn/excel-upload",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      data: data.data,
      headers: data.headers,
      tableName: tableName,
    },
    useLoading: false,
  });
};

/**
 * summary: Get Student List
 * #GET./api/v1/auth/students
 */
export const getStudentList = async (
  params: ParamsType,
): Promise<StudentType[]> => {
  return await apiRequest<StudentType[]>({
    url: `/api/v1/auth/students`,
    params: params,
  });
};

/**
 * summary: Get Staff List
 * #GET./api/v1/auth/staffs
 */
export const getStaffList = async (
  params: ParamsType,
): Promise<StaffType[]> => {
  return await apiRequest<StaffType[]>({
    url: `/api/v1/auth/staffs`,
    params: params,
  });
};

/**
 * summary: Get All Student List(재학생+SNS)
 * #GET./api/v1/auth/students-all
 */
export const getAllStudentList = async (
  params: ParamsType,
): Promise<StudentType[]> => {
  return await apiRequest<StudentType[]>({
    url: "/api/v1/auth/students-all",
    params: params,
  });
};

/**
 * summary: Get All Staff List(교직원+외부강사/외부상담사)
 * #GET./api/v1/staff/ecp/oper/staffs-all
 */
export const getAllStaffList = async (
  params: ParamsType,
): Promise<StaffType[]> => {
  return await apiRequest<StaffType[]>({
    url: "/api/v1/auth/staffs-all",
    params: params,
  });
};

/**
 * summary: Get Dept List
 * #GET./api/v1/cmmn/depts
 * @param params.deptDiv: edu: 교육조직 | adm: 행정조직
 * @param params.deptLevel: 공통으로 같은 레벨 조회 시 사용
 * @param params.eduLevel: 교육조직 레벨 조회 시 사용
 * @param params.admLevel: 행정조직 레벨 조회 시 사용
 * ----------- edu ----------
 * 1: 학과의 상위 그룹(COLEAGE)
 * 2: 학과(HAKGUA)
 * 3: 전공(MAJOR)
 * --------------------------
 * ----------- adm ----------
 * 1: 상위 그룹(UP_DEPT)
 * 2: 부서,과(DEPT)
 * --------------------------
 */
export const getDeptList = async (params: ParamsType): Promise<DeptType[]> => {
  return await apiRequest<DeptType[]>({
    url: `/api/v1/cmmn/depts`,
    params: params,
  });
};

export const getAbilityList = async (
  params: ParamsType,
): Promise<AbilityTypes[]> => {
  return await apiRequest<AbilityTypes[]>({
    url: `/api/v1/cmmn/ability`,
    params: params,
  });
};

export const getAbilityOptions = async (): Promise<CmmnCodeOptionType[]> => {
  return await apiRequest<CmmnCodeOptionType[]>({
    url: `/api/v1/cmmn/ability-options`,
  });
};

/**
 * summary: Get Ecp Category Options
 * #GET./api/v1/cmmn/ecp-category-options
 * @param params - Optional parameters (upCategoryCd: 상위 카테고리 코드로 필터링)
 */
export const getCategoryOptions = async (
  params?: ParamsType,
): Promise<CmmnCodeOptionType[]> => {
  return await apiRequest<CmmnCodeOptionType[]>({
    url: `/api/v1/cmmn/category-options`,
    params: params,
  });
};

/**
 * summary: Get School Year Options
 * #GET./api/v1/cmmn/year-options
 * @param params
 */
export const getYearOptions = async (): Promise<CmmnCodeOptionType[]> => {
  return await apiRequest<CmmnCodeOptionType[]>({
    url: `/api/v1/cmmn/year-options`,
  });
};

/**
 * summary: Merge Favorite (찜/좋아요 merge)
 * Merge Favorite (Wishlist/Like merge)
 * #POST./api/v1/auth/favorite
 * @param favoriteId - 찜/좋아요 key ID | Favorite/Like key ID
 * @param userDiv - 사용자 구분 (student, staff) | User division (student, staff)
 * @param div - 메뉴 구분 (eg. ECP, RECRUIT) | Menu division (eg. ECP, RECRUIT)
 * @param cont - 관심 구분(찜: W, 좋아요: L) | Interest type (Wishlist: W, Like: L)
 * @param referId - 게시글 ID | Reference ID
 */
export const mergeFavorite = async (
  data: ParamsType,
): Promise<CudResultType> => {
  return await apiRequest<CudResultType>({
    method: ApiMethod.POST,
    url: "/api/v1/auth/favorite",
    data: data,
    useLoading: false,
  });
};

/**
 * summary: Insert/Update View Count
 * #POST./api/v1/cmmn/view-count
 * @param viewCountId - 조회수 ID | View count ID
 * @param categoryCd - 카테고리 코드(메뉴, 비교과: ECP) | Category code (Menu category code)
 * @param typeCd - 유형 코드(DETAIL, ASSIGN) | Type code (DETAIL, ASSIGN)
 * @param referId - 게시글 ID | Reference ID
 */
export const mergeViewCount = async (data: ParamsType): Promise<number> => {
  return await apiRequest<number>({
    method: ApiMethod.POST,
    url: "/api/v1/cmmn/view-count",
    data: data,
  });
};

/**
 * summary: Get Cur Year/Semester
 * #GET./api/v1/cmmn/current-year-semester
 */
export const getCurYearSemester = async (): Promise<{
  year: string;
  semesterCd: string;
}> => {
  return await apiRequest<{ year: string; semesterCd: string }>({
    url: `/api/v1/cmmn/current-year-semester`,
  });
};

/**
 * summary: Send SMS / SMS 발송
 * #POST./api/v1/cmmn/send/sms
 * @param type - user_list (고정)
 * @parm userId[] - 학번
 * @param callback - 발신번호 (선택, 숫자만 / 미입력 시 서버 기본값)
 */
export const sendSms = async (data: {
  type: "user_list";
  userId: string[];
  message: string;
  callback?: string;
}): Promise<number> => {
  return await apiRequest<number>({
    method: ApiMethod.POST,
    url: _url + "/send/sms",
    data,
  });
};

export default {
  getCmmnList,
  getCmmnCdOption,
  getStudentList,
  getStaffList,
  sendSms,
};
